import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { X, Send, CheckCircle2 } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import CountrySelect, { COUNTRIES } from './CountrySelect';
import api from '../../lib/api';

export default function EnquiryModal({ open, onOpenChange, context }) {
  const { data } = useSite();
  const { user } = useAuth();

  const [name, setName] = useState('');
  const [country, setCountry] = useState(COUNTRIES[0]);
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (user) {
      setName(user.name || '');
      // try parse mobile if stored
      const m = user.mobile || '';
      const found = COUNTRIES.find((c) => m.startsWith(c.code.replace('+', '')));
      if (found) {
        setCountry(found);
        setMobile(m.replace(found.code.replace('+', ''), ''));
      } else {
        setMobile(m);
      }
    }
  }, [open, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Name is required');
    if (mobile.length < 6) return toast.error('Enter a valid mobile number');
    setLoading(true);
    try {
      await api.post('/enquiry', {
        name: name.trim(),
        country: country.code,
        mobile,
        email: email.trim(),
        note: note.trim(),
        product_type: context?.product?.id || '',
        product_name: context?.item?.name || '',
        source: 'website'
      });
      setSuccess(true);
      toast.success('Enquiry submitted successfully! We will get back to you shortly.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit enquiry');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSuccess(false);
    setName(''); setMobile(''); setEmail(''); setNote('');
  };

  const close = () => { onOpenChange(false); setTimeout(reset, 300); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v ? close() : onOpenChange(v)}>
      <DialogContent className="sm:max-w-[460px] bg-[#1a1a1a] border border-zinc-800 text-zinc-100 p-0 sm:rounded-xl [&>button]:hidden">
        <DialogTitle className="sr-only">Send Enquiry</DialogTitle>
        <DialogDescription className="sr-only">Submit an enquiry about our products and services</DialogDescription>
        <span className="absolute right-3 top-3 z-20">
          <button onClick={close} className="w-8 h-8 rounded-full text-zinc-300 hover:text-white bg-black/30 hover:bg-red-500/80 border border-white/10 flex items-center justify-center transition" data-testid="enquiry-close-btn" aria-label="Close enquiry">
            <X size={18} />
          </button>
        </span>

        {success ? (
          <div className="px-6 py-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-4 float-anim">
              <CheckCircle2 size={36} className="text-emerald-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Enquiry Received!</h3>
            <p className="text-zinc-400 text-sm mb-2">Thank you, <span className="text-[color:var(--gold)] font-bold">{name}</span>.</p>
            <p className="text-zinc-400 text-sm mb-6">Our sales team will contact you at +{country.code.replace('+', '')} {mobile} within 24 hours.</p>
            <a href={data.whatsappLink} target="_blank" rel="noreferrer" className="inline-flex h-11 px-6 rounded-md bg-[#25d366] hover:bg-[#1ebd5d] text-white font-bold text-sm items-center gap-2">
              Talk to Sales on WhatsApp
            </a>
            <button onClick={close} className="block mx-auto mt-4 text-zinc-500 hover:text-zinc-300 text-sm">Close</button>
          </div>
        ) : (
          <>
            <div className="px-5 pt-4 pb-1">
              <h2 className="text-2xl font-bold text-white">Send Enquiry</h2>
              {context?.item?.name && (
                <p className="text-zinc-400 text-sm mt-1">
                  About: <span className="text-[color:var(--gold)] font-semibold">{context.item.name}</span>
                  {context.product?.label && <span className="text-zinc-500"> ({context.product.label})</span>}
                </p>
              )}
            </div>

            <form onSubmit={submit} className="px-5 pb-5 space-y-2.5">
              <div>
                <label className="text-[12px] font-semibold text-zinc-300 mb-1 block">Your Name *</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full Name" className="h-11 bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="enquiry-name-input" />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-zinc-300 mb-1 block">Mobile *</label>
                <div className="flex items-stretch bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden focus-within:border-[color:var(--gold)]/50">
                  <CountrySelect value={country} onChange={setCountry} />
                  <input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 15))} placeholder="Mobile Number" className="flex-1 h-11 bg-transparent px-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none" data-testid="enquiry-mobile-input" />
                </div>
              </div>
              <div>
                <label className="text-[12px] font-semibold text-zinc-300 mb-1 block">Email (optional)</label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com" className="h-11 bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="enquiry-email-input" />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-zinc-300 mb-1 block">Message</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tell us what you need..." rows={2} className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-[color:var(--gold)]/40 resize-none" data-testid="enquiry-note-input" />
              </div>
              <button type="submit" disabled={loading} className="w-full h-11 rounded-md bg-[color:var(--gold)] hover:brightness-110 disabled:opacity-60 text-black font-bold text-[15px] flex items-center justify-center gap-2 click-ripple" data-testid="enquiry-submit-btn">
                <Send size={16} /> {loading ? 'Sending...' : 'Submit Enquiry'}
              </button>
              <p className="text-[11px] text-zinc-500 text-center">Your details are securely stored. Sales team will contact within 24 hours.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
